/**
 * /yc/claim — Post-auth landing for the YC invite flow.
 *
 * Reached by clicking the magic link in the YC welcome email. By the
 * time we render here:
 *   • the user has a session cookie (verify already ran)
 *   • the URL still carries `?code=YC-2026` from the magic link
 *
 * We POST /api/redeem with that code, which grants 6 months of Pro
 * (multi-use code, capped at 3 unique redemptions). On success, we
 * land the user on /home with a celebration toast. On failure (cap
 * reached, code revoked, etc.) we show the backend's error message
 * and let them retry or go home.
 *
 * Why a separate page instead of redeeming inside MagicLinkVerify?
 *   • Cleaner separation: MagicLinkVerify is reused by every magic
 *     link (login + invite + email change). Putting redemption logic
 *     there would couple every magic-link entry point to gift codes.
 *   • Better UX: a dedicated landing page can show "redeeming…" copy
 *     and a celebratory success state. A generic verify page can't.
 *   • Resilient to errors: if redemption fails (e.g. cap hit), the
 *     user still has a valid session and can navigate the app.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Sparkles, AlertTriangle, ArrowRight, Loader2, Mail } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const YCClaim = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, checkAuth } = useAuth();
  const ranOnce = useRef(false);

  const code = useMemo(() => {
    const raw = (params.get("code") || "YC-2026").trim();
    return raw.toUpperCase().slice(0, 40);
  }, [params]);

  const [state, setState] = useState({ status: "claiming", message: null });

  // Cap-reached waitlist form state. Pre-fills the email from the
  // signed-in user so the YC visitor doesn't have to retype it.
  const [waitlist, setWaitlist] = useState({
    name: "",
    org: "",
    role: "founder",
    message: "",
    sending: false,
    sent: false,
  });
  // Prefill the name from the auth user once we have it. Email is
  // always taken from `user.email` at submit time, not stored in
  // state, so a stale tab can't post the wrong address.
  useEffect(() => {
    if (user?.name && !waitlist.name) {
      setWaitlist((prev) => ({ ...prev, name: user.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!user?.email) return;
    setWaitlist((prev) => ({ ...prev, sending: true }));
    try {
      await axios.post(
        `${API}/yc-waitlist`,
        {
          email: user.email,
          name: waitlist.name.trim() || (user.name || "").trim() || "YC visitor",
          role: waitlist.role || "founder",
          org: waitlist.org.trim() || undefined,
          message: waitlist.message.trim() || undefined,
          source: "cap_reached",
        },
        { withCredentials: true },
      );
      setWaitlist((prev) => ({ ...prev, sending: false, sent: true }));
      toast.success("You're on the list. I'll personally onboard you within 24h.");
    } catch (err) {
      setWaitlist((prev) => ({ ...prev, sending: false }));
      const detail = err?.response?.data?.detail;
      toast.error(
        (typeof detail === "string" && detail) ||
          "Couldn't save your spot. Email hello@trubeaute.com and I'll get you in.",
      );
    }
  };

  useEffect(() => {
    // StrictMode (and the magic-link useRef pattern in MagicLinkVerify)
    // can cause this effect to fire twice — but our redeem endpoint is
    // idempotent (409 "already redeemed by you" is treated as success
    // below) so it's safe. We still guard with a ref to avoid the
    // visible "claiming → error → claiming → success" flicker.
    if (ranOnce.current) return;
    ranOnce.current = true;

    if (!user) {
      // No session — push the visitor back to the YC landing so they
      // can request a magic link.
      navigate(`/yc?code=${encodeURIComponent(code)}`, { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/redeem`,
          { code },
          { withCredentials: true },
        );
        setState({
          status: "claimed",
          message: data?.message || "Pro is yours.",
        });
        toast.success(data?.message || "Welcome to Pro 💜");
        // Refresh /me so Pro flags propagate to the rest of the app
        // (header pro pill, paywall guards, etc.) before we navigate.
        try { await checkAuth?.(); } catch { /* non-fatal */ }
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const code409 = err?.response?.status === 409;
        // "Already redeemed by you" is success from the user's POV.
        const alreadyClaimed =
          code409 &&
          typeof detail === "string" &&
          /already redeemed|pro is active/i.test(detail);
        if (alreadyClaimed) {
          setState({
            status: "claimed",
            message: "You're already in. Pro is active on your account.",
          });
          try { await checkAuth?.(); } catch { /* non-fatal */ }
          return;
        }
        // Cap reached → show the waitlist form instead of a dead-end
        // error. Backend now returns a structured detail dict on cap.
        const capReached =
          code409 &&
          typeof detail === "object" &&
          detail?.reason === "redemption_cap_reached";
        if (capReached) {
          setState({
            status: "cap_reached",
            message:
              detail?.message ||
              "This invite has reached its limit — but I'll personally onboard you.",
          });
          return;
        }
        setState({
          status: "error",
          message:
            (typeof detail === "string" && detail) ||
            (typeof detail === "object" && detail?.message) ||
            "We couldn't apply your invite. Reach out and we'll sort it out.",
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-12 bg-gradient-to-br from-[#FFF8F1] via-[#FFE9D4] to-[#FFC58A]"
      data-testid="yc-claim-root"
    >
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-lg w-full bg-white/90 backdrop-blur-sm rounded-3xl soft-shadow-lg p-8 sm:p-10 text-center"
      >
        <div className="flex items-center justify-center gap-3 mb-6">
          <div
            className="h-10 w-10 rounded-md bg-[#FF6600] flex items-center justify-center text-white font-bold text-xl"
            aria-hidden="true"
          >
            Y
          </div>
        </div>

        {state.status === "claiming" && (
          <>
            <Loader2 className="h-8 w-8 text-[#FF6600] animate-spin mx-auto mb-4" />
            <h1 className="font-serif italic text-2xl sm:text-3xl text-[#2C2123] mb-2">
              Activating your Pro access…
            </h1>
            <p className="text-[#5C4B51] text-sm">
              One moment — applying your YC invite.
            </p>
          </>
        )}

        {state.status === "claimed" && (
          <div data-testid="yc-claim-success">
            <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#FFB36B] to-[#FF6600] flex items-center justify-center mx-auto mb-5 soft-shadow">
              <Sparkles className="h-7 w-7 text-white" />
            </div>
            <h1 className="font-serif italic text-3xl text-[#2C2123] mb-3">
              You're in.
            </h1>
            <p className="text-[#5C4B51] text-base leading-relaxed mb-7 max-w-sm mx-auto">
              {state.message}
            </p>
            <button
              onClick={() => navigate("/home", { replace: true })}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF6600] text-white font-medium hover:bg-[#E55A00] transition-all"
              data-testid="yc-claim-cta"
            >
              Open my home
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {state.status === "cap_reached" && (
          <div data-testid="yc-cap-reached">
            <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#FFB36B] to-[#FF6600] flex items-center justify-center mx-auto mb-5 soft-shadow">
              <Sparkles className="h-7 w-7 text-white" />
            </div>
            <h1 className="font-serif italic text-2xl sm:text-3xl text-[#2C2123] mb-3 text-center">
              Let me onboard you personally.
            </h1>
            <p className="text-[#5C4B51] text-base leading-relaxed mb-6 max-w-md mx-auto text-center">
              {state.message} Drop your details and I'll grant you Pro
              + a 1:1 walkthrough within 24 hours.
            </p>

            {waitlist.sent ? (
              <div
                className="rounded-2xl border border-[#FFD9B0] bg-[#FFF4E6] p-5 text-sm text-[#5C4B51] text-left"
                data-testid="yc-waitlist-sent"
              >
                <p className="font-medium text-[#2C2123] mb-1">
                  You're on the list.
                </p>
                <p>
                  I'll personally reply to{" "}
                  <span className="font-medium text-[#FF6600]">{user?.email}</span>{" "}
                  within 24 hours with your Pro access. In the meantime,
                  feel free to browse — the home tour and free tier are
                  open to you.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/home", { replace: true })}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2C2123] text-white text-sm font-medium hover:bg-[#3a2b2e] transition-all"
                  data-testid="yc-cap-go-home"
                >
                  Continue exploring
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleWaitlistSubmit}
                className="space-y-3 text-left"
                data-testid="yc-waitlist-form"
              >
                {/* Honeypot — display:none so humans never see/fill it.
                    Any non-empty value silently returns success without
                    persisting on the backend side. */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  style={{ position: "absolute", left: "-9999px", height: 0, width: 0 }}
                  aria-hidden="true"
                  onChange={() => { /* honeypot — ignored client-side */ }}
                />

                <div>
                  <label
                    htmlFor="wl-name"
                    className="block text-xs uppercase tracking-[0.18em] text-[#5C4B51] font-medium mb-1.5"
                  >
                    Your name
                  </label>
                  <input
                    id="wl-name"
                    type="text"
                    value={waitlist.name}
                    onChange={(e) => setWaitlist((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Jordan Lee"
                    required
                    className="w-full px-4 py-2.5 rounded-full border border-[#EADDD7] bg-white text-[#2C2123] placeholder-[#A89798] focus:outline-none focus:ring-2 focus:ring-[#FF6600] focus:border-transparent transition-all"
                    data-testid="yc-waitlist-name"
                    disabled={waitlist.sending}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor="wl-role"
                      className="block text-xs uppercase tracking-[0.18em] text-[#5C4B51] font-medium mb-1.5"
                    >
                      Role
                    </label>
                    <select
                      id="wl-role"
                      value={waitlist.role}
                      onChange={(e) => setWaitlist((prev) => ({ ...prev, role: e.target.value }))}
                      className="w-full px-4 py-2.5 rounded-full border border-[#EADDD7] bg-white text-[#2C2123] focus:outline-none focus:ring-2 focus:ring-[#FF6600] focus:border-transparent transition-all"
                      data-testid="yc-waitlist-role"
                      disabled={waitlist.sending}
                    >
                      <option value="founder">YC founder</option>
                      <option value="partner">YC partner</option>
                      <option value="alum">YC alum</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="wl-org"
                      className="block text-xs uppercase tracking-[0.18em] text-[#5C4B51] font-medium mb-1.5"
                    >
                      Batch / company
                    </label>
                    <input
                      id="wl-org"
                      type="text"
                      value={waitlist.org}
                      onChange={(e) => setWaitlist((prev) => ({ ...prev, org: e.target.value }))}
                      placeholder="W26 · CompanyCo"
                      className="w-full px-4 py-2.5 rounded-full border border-[#EADDD7] bg-white text-[#2C2123] placeholder-[#A89798] focus:outline-none focus:ring-2 focus:ring-[#FF6600] focus:border-transparent transition-all"
                      data-testid="yc-waitlist-org"
                      disabled={waitlist.sending}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="wl-message"
                    className="block text-xs uppercase tracking-[0.18em] text-[#5C4B51] font-medium mb-1.5"
                  >
                    Anything you'd like me to know? (optional)
                  </label>
                  <textarea
                    id="wl-message"
                    value={waitlist.message}
                    onChange={(e) => setWaitlist((prev) => ({ ...prev, message: e.target.value }))}
                    rows={3}
                    placeholder="e.g. would love to see the AR mirror demo, building in adjacent space, etc."
                    className="w-full px-4 py-2.5 rounded-2xl border border-[#EADDD7] bg-white text-[#2C2123] placeholder-[#A89798] focus:outline-none focus:ring-2 focus:ring-[#FF6600] focus:border-transparent transition-all resize-none"
                    data-testid="yc-waitlist-message"
                    disabled={waitlist.sending}
                    maxLength={2000}
                  />
                </div>

                <div className="text-xs text-[#8A7D82] flex items-center gap-1.5">
                  <Mail className="h-3 w-3" />
                  I'll reply to <span className="font-medium">{user?.email}</span>.
                </div>

                <button
                  type="submit"
                  disabled={waitlist.sending || !user?.email}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#FF6600] text-white font-medium hover:bg-[#E55A00] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                  data-testid="yc-waitlist-submit"
                >
                  {waitlist.sending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving your spot…
                    </>
                  ) : (
                    <>
                      Onboard me personally
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        )}

        {state.status === "error" && (
          <div data-testid="yc-claim-error">
            <div className="h-14 w-14 rounded-full bg-[#FFE6E0] flex items-center justify-center mx-auto mb-5">
              <AlertTriangle className="h-7 w-7 text-[#C45F4C]" />
            </div>
            <h1 className="font-serif italic text-2xl sm:text-3xl text-[#2C2123] mb-3">
              We hit a snag.
            </h1>
            <p className="text-[#5C4B51] text-base leading-relaxed mb-7 max-w-sm mx-auto">
              {state.message}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate("/home", { replace: true })}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2C2123] text-white font-medium hover:bg-[#3a2b2e] transition-all"
              >
                Continue to TruBeauté
                <ArrowRight className="h-4 w-4" />
              </button>
              <a
                href="mailto:hello@trubeaute.com?subject=YC%20invite%20issue"
                className="text-sm text-[#5C4B51] hover:text-[#9B6A6C] underline-offset-2 hover:underline transition-colors"
              >
                Email the founder
              </a>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default YCClaim;
